import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../AppContext'; // Import the context hook
import { useTranslation } from 'react-i18next';

const FTUE_TASKS = [
  { id: 'visited_history', text: 'ftueChecklist.tasks.visitedHistory' },
  { id: 'visited_quests', text: 'ftueChecklist.tasks.visitedQuests' },
  { id: 'opened_echo_chamber', text: 'ftueChecklist.tasks.openedEchoChamber' },
];

const FTUEChecklist = ({ activeTab }) => {
  const { currentUserProfile } = useAppContext(); // Get the user profile from context
  const { t } = useTranslation();
  const [completedTasks, setCompletedTasks] = useState(() => {
    const saved = localStorage.getItem('ftueWalletTasks');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [isVisible, setIsVisible] = useState(true);

  // Function to update state and localStorage
  const completeTask = (taskId) => {
    setCompletedTasks(prev => {
      const newTasks = new Set(prev);
      newTasks.add(taskId);
      return newTasks;
    });
  };

  useEffect(() => {
    // Save to localStorage whenever completedTasks changes
    localStorage.setItem('ftueWalletTasks', JSON.stringify([...completedTasks]));
  }, [completedTasks]);

  useEffect(() => {
    // Task 1: Check history tab visit
    if (activeTab === 'history' && !completedTasks.has('visited_history')) {
      completeTask('visited_history');
    }
    // Task 2: Check quests tab visit
    if (activeTab === 'quests' && !completedTasks.has('visited_quests')) {
      completeTask('visited_quests');
    }
  }, [activeTab, completedTasks]);

  useEffect(() => {
    // Task 3: Check if the echo chamber has ever been opened
    if (currentUserProfile?.lastChamberOpen && !completedTasks.has('opened_echo_chamber')) {
      completeTask('opened_echo_chamber');
    }
  }, [currentUserProfile, completedTasks]);

  const isComplete = completedTasks.size >= FTUE_TASKS.length;

  if (!isVisible || isComplete) {
    return null;
  }

  return (
    <div className="bg-gray-800/80 backdrop-blur-sm border border-indigo-500/50 rounded-lg p-4 my-4 animate-fadeInUp relative">
      <button onClick={() => setIsVisible(false)} className="absolute top-2 right-2 text-gray-500 hover:text-white">
        <LucideIcons.X size={16} />
      </button>
      <h3 className="text-md font-bold text-indigo-300 mb-2">{t('ftueChecklist.title')}</h3>
      <ul className="space-y-2">
        {FTUE_TASKS.map(task => (
          <li key={task.id} className="flex items-center text-sm">
            {completedTasks.has(task.id) ? (
              <LucideIcons.CheckCircle2 size={16} className="mr-2 text-green-400" />
            ) : (
              <LucideIcons.Circle size={16} className="mr-2 text-gray-500" />
            )}
            <span className={`${completedTasks.has(task.id) ? 'text-gray-400 line-through' : 'text-white'}`}>
              {t(task.text)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default FTUEChecklist;